import React, { useState } from "react";
import { Loader2, Sparkles, Check, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { userTimezone } from "@/lib/time";

export function proposalToPayload(p) {
  const base = { title: p.title, category: p.category || "PERSONAL", transparency: p.transparency || "busy", location: p.location || "", timezone: userTimezone() };
  if (p.all_day) return { ...base, all_day: true, start_date: p.date };
  const start = new Date(`${p.date}T${p.start_time || "09:00"}:00`);
  const end = p.end_time ? new Date(`${p.date}T${p.end_time}:00`) : new Date(start.getTime() + 3600000);
  return { ...base, start: start.toISOString(), end: end.toISOString() };
}

export function ProposalCard({ p, onChange }) {
  return (
    <div className={`rounded-xl border p-3 text-sm space-y-2 ${p.ambiguous ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card"}`} data-testid="event-proposal">
      <div className="flex items-center gap-2">
        <input value={p.title} onChange={(e) => onChange({ ...p, title: e.target.value })} className="flex-1 bg-transparent font-medium outline-none border-b border-transparent focus:border-border" />
        <span className={`font-mono text-[9px] px-1.5 py-0.5 rounded-full border ${p.transparency === "free" ? "text-emerald-300 border-emerald-500/30" : "text-rose-300 border-rose-500/30"}`}>{p.transparency === "free" ? "LIBRE" : "OCCUPÉ"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 font-mono text-xs">
        <input type="date" value={p.date} onChange={(e) => onChange({ ...p, date: e.target.value })} className="rounded-md border border-border bg-background px-2 py-1" />
        {!p.all_day && <><input type="time" value={p.start_time} onChange={(e) => onChange({ ...p, start_time: e.target.value })} className="rounded-md border border-border bg-background px-2 py-1" />
          <span>–</span><input type="time" value={p.end_time || ""} onChange={(e) => onChange({ ...p, end_time: e.target.value })} className="rounded-md border border-border bg-background px-2 py-1" /></>}
        {p.all_day && <span className="text-muted-foreground">journée entière</span>}
        <span className="text-muted-foreground">· {p.category}</span>
      </div>
      {p.ambiguous && <div className="flex items-start gap-1.5 text-xs text-amber-300"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {p.question || "Date ou heure ambiguë — vérifie avant de confirmer."}</div>}
    </div>
  );
}

export default function BulkEntryDialog({ open, onClose, onCreated }) {
  const [text, setText] = useState("");
  const [props, setProps] = useState(null);
  const [busy, setBusy] = useState(false);

  const parse = async () => {
    setBusy(true);
    try { const res = await api.post("/calendar/events/parse", { text }); setProps(res.data.events); if (!res.data.events.length) toast.info("Aucun événement reconnu."); }
    catch { toast.error("Analyse impossible"); } finally { setBusy(false); }
  };
  const confirmAll = async () => {
    setBusy(true);
    try {
      await api.post("/calendar/events/bulk", { events: props.map(proposalToPayload) });
      toast.success(`${props.length} événement(s) ajouté(s) au calendrier AIRTIME`);
      onCreated?.(); setText(""); setProps(null); onClose();
    } catch { toast.error("Création impossible"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setProps(null); } }}>
      <DialogContent className="max-w-lg" data-testid="bulk-entry-dialog">
        <DialogHeader><DialogTitle className="font-display">Saisie rapide de plusieurs événements</DialogTitle></DialogHeader>
        {!props ? (
          <div className="space-y-3">
            <textarea data-testid="bulk-text" value={text} onChange={(e) => setText(e.target.value)} rows={5}
              placeholder={"Demain 10h dentiste 1h\nJeudi 14h déjeuner Julien 2h\nVendredi 9h golf 5h"}
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm resize-none" />
            <div className="text-xs text-muted-foreground">Une ligne par événement. Pratique pour recopier des rendez-vous depuis Apple Calendar.</div>
            <div className="flex justify-end"><button data-testid="bulk-parse" onClick={parse} disabled={busy || !text.trim()} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyser</button></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground" data-testid="bulk-count">{props.length} événement(s) trouvé(s)</div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto airtime-scroll pr-1">{props.map((p, i) => <ProposalCard key={i} p={p} onChange={(np) => setProps(props.map((x, j) => j === i ? np : x))} />)}</div>
            <div className="flex justify-between">
              <button onClick={() => setProps(null)} className="rounded-full border border-border px-4 py-2 text-sm hover:bg-accent">Retour</button>
              <button data-testid="bulk-confirm-all" onClick={confirmAll} disabled={busy || props.length === 0} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Tout confirmer</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
